import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { heroContentPropType, productPropType } from '@/lib/propTypes';

const animationEase = [0.22, 1, 0.36, 1];

const fallbackHeroContent = {
  eyebrow: 'Trendy Quest Collection',
  title: 'Comfortable shoes for everyday style.',
  description:
    'Quality, trendy footwear at wholesale prices, even from one pair.',
  primaryCtaLabel: 'Shop now',
};

const fallbackHeroSlides = ['Women', 'Men', 'Kids', 'Workwear'].map((category) => ({
  id: `homepage-collection-${category.toLowerCase()}`,
  name: `Explore ${category} footwear`,
  category,
  image: '/homepage banner.jpg',
  isCollectionFallback: true,
}));

const formatPrice = (price) => {
  if (price === undefined || price === null || price === '') return null;
  const numericPrice = Number(price);
  if (Number.isNaN(numericPrice)) return `KES ${price}`;
  return `KES ${numericPrice.toLocaleString()}`;
};

export default function EditorialHeroSection({ products = [], homeHeroContent }) {
  const [activeIndex, setActiveIndex] = useState(0);

  const carouselProducts = useMemo(
    () => {
      const availableProducts = products
        .filter((product) => product.image && product.name)
        .slice(0, 5);
      return availableProducts.length ? availableProducts : fallbackHeroSlides;
    },
    [products]
  );

  const heroContent = {
    eyebrow: homeHeroContent?.eyebrow || fallbackHeroContent.eyebrow,
    title: homeHeroContent?.title || fallbackHeroContent.title,
    description: homeHeroContent?.description || fallbackHeroContent.description,
    primaryCtaLabel: homeHeroContent?.primaryCtaLabel || fallbackHeroContent.primaryCtaLabel,
  };

  useEffect(() => {
    if (carouselProducts.length <= 1) return undefined;

    const timer = globalThis.window?.setTimeout(() => {
      setActiveIndex((current) => (current + 1) % carouselProducts.length);
    }, 6000);

    return () => globalThis.window?.clearTimeout(timer);
  }, [activeIndex, carouselProducts.length]);

  useEffect(() => {
    if (activeIndex >= carouselProducts.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, carouselProducts.length]);

  if (!carouselProducts.length) {
    return null;
  }

  const activeProduct = carouselProducts[activeIndex];

  return (
    <section className="relative overflow-hidden bg-[linear-gradient(180deg,#f8f5ef_0%,#fffdfa_100%)]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-0 h-[24rem] w-[24rem] rounded-full bg-[#f5ede1] blur-[120px]" />
        <div className="absolute right-[-6rem] top-12 h-[22rem] w-[22rem] rounded-full bg-[#eef4ed] blur-[120px]" />
      </div>

      <div className="relative mx-auto grid max-w-[1440px] items-center gap-10 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-[0.95fr_1.05fr] lg:gap-14 lg:py-20">
        <div className="order-2 lg:order-1">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: animationEase }}
            className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#7a7165]"
          >
            {heroContent.eyebrow}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.08, ease: animationEase }}
            className="mt-4 max-w-[12ch] font-playfair text-4xl leading-tight text-[#171717] sm:text-5xl lg:text-6xl"
          >
            {heroContent.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.14, ease: animationEase }}
            className="mt-5 max-w-xl text-base leading-8 text-[#5f5a54] sm:text-lg"
          >
            {heroContent.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.2, ease: animationEase }}
            className="mt-8 flex flex-col items-start gap-4"
          >
            <Link
              href="/products"
              className="inline-flex w-full items-center justify-center rounded-full bg-[#171717] px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-[#2b2622] sm:w-auto sm:px-8 sm:py-4 sm:tracking-[0.14em]"
            >
              {heroContent.primaryCtaLabel}
            </Link>
          </motion.div>
        </div>

        <motion.div
          key={activeProduct.id}
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: animationEase }}
          className="order-1 lg:order-2"
        >
          <div className="relative overflow-hidden rounded-[2rem] border border-[#eadfce] bg-white shadow-[0_24px_60px_rgba(17,17,17,0.08)]">
            <div className="absolute left-6 top-6 z-10 rounded-full bg-white/92 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#2b2a27] shadow-sm">
              {activeProduct.category || 'Featured'}
            </div>

            <div className="relative aspect-[4/3] bg-[linear-gradient(180deg,#f6f0e7_0%,#fcfaf6_100%)]">
              <Image
                src={activeProduct.image}
                alt={activeProduct.name}
                fill
                priority={activeIndex === 0}
                sizes="(max-width: 1024px) 100vw, 48vw"
                className="object-cover"
              />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(0,0,0,0.12))]" />
            </div>

            <div className="flex items-center justify-between gap-4 border-t border-[#efe6d9] px-5 py-5 sm:px-6">
              <div>
                <h2 className="font-playfair text-2xl text-[#171717]">{activeProduct.name}</h2>
                <p className="mt-1 text-sm text-[#6d675f]">
                  {activeProduct.isCollectionFallback
                    ? 'Discover styles for every step'
                    : formatPrice(activeProduct.price) || 'Available in store'}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setActiveIndex((current) =>
                      (current - 1 + carouselProducts.length) % carouselProducts.length
                    )
                  }
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#d8cebf] bg-white text-[#171717] transition hover:border-[#bca98a]"
                  aria-label="Previous featured product"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setActiveIndex((current) => (current + 1) % carouselProducts.length)}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#d8cebf] bg-white text-[#171717] transition hover:border-[#bca98a]"
                  aria-label="Next featured product"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

EditorialHeroSection.propTypes = {
  products: PropTypes.arrayOf(productPropType),
  homeHeroContent: heroContentPropType,
};
